import { useState, useEffect, useContext } from "react";
import Context from "../../scripts/context/User";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import { validationSchema } from "./validationSchema";

import CustomLink from "../../components/Link";
import VerificationInput from "react-verification-input";

export default function ResetPassword() {
  const { setGlobalState, getRegistrationStep } = useContext(Context);
  let [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState({
    email: "",
    password: "",
    password_confirmation: "",
    youCanChangePassword: false,
    one_time_password: null,
  });
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [twoFADialog, setTwoFADialog] = useState(false);

  const handleChange = (event) =>
    setState({
      ...state,
      [event.target.name]: event.target.value,
    });

  const isError = (field) => errors.find((e) => e.name === field);
  const getErrorMessage = (field) =>
    errors.find((e) => e.name === field)
      ? errors.find((e) => e.name === field).message
      : "";

  useEffect(() => {
    if (searchParams.get("reset_token") && searchParams.get("email")) {
      setState({
        ...state,
        youCanChangePassword: true,
        email: searchParams.get("email"),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSubmit = (event) => {
    event.preventDefault();

    validationSchema
      .validate(state, { abortEarly: false })
      .then(() => {
        setErrors([]);
        setLoading(true);

        if (state.youCanChangePassword) {
          if (searchParams.get("two_fa") && !state.one_time_password) {
            setTwoFADialog(true);
            setLoading(false);
          } else {
            axios
              .post("/saveRecoveryPassword", {
                email: searchParams.get("email"),
                password: state.password,
                password_confirmation: state.password_confirmation,
                reset_token: searchParams.get("reset_token"),
                ...(state.one_time_password && {
                  one_time_password: state.one_time_password,
                }),
              })
              .then(({ data }) => {
                toast.success(data.message);
                localStorage.setItem("token", data.token);
                setGlobalState({
                  user: {
                    ...data.user,
                  },
                  redirect_uri: data.redirect_uri,
                  registration_step: getRegistrationStep(data.redirect_uri),
                });
                data.redirect_uri && navigate(data.redirect_uri);
              })
              .catch(({ response }) => {
                setLoading(false);
                toast.error(response.data.message);
                setSearchParams({});
              });
          }
        } else {
          axios
            .post("/sendRecoveryPassword", {
              email: state.email,
            })
            .then(({ data }) => {
              toast.success(data.message);
              setState({
                ...state,
                email: "",
              });
              setLoading(false);
            })
            .catch(({ response }) => {
              setLoading(false);
              toast.error(response.data.message);
            });
        }
      })
      .catch((err) => {
        setLoading(false);
        const errors = err.inner.map((e) => {
          return {
            name: e.path,
            message: e.message,
          };
        });
        setErrors(errors);
      });
  };

  const handleCloseDialog = () => {
    setTwoFADialog(false);
    setState({
      ...state,
      one_time_password: "",
    });
  };

  return (
    <div className="d-flex flex-column flex-root">
      <Dialog
        open={twoFADialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={{
          ".MuiPaper-root": {
            backgroundColor: "rgb(3 45 107)",
            color: "#fff",
            maxWidth: "600px",
            width: "100%",
            alignItems: "center",
            borderRadius: "20px",
          },
          ".MuiDialogContent-root": {
            pt: "1rem !important",
          },
        }}
      >
        <DialogTitle id="alert-dialog-title">
          Enter Your Google Authentication Code
        </DialogTitle>
        <DialogContent>
          <VerificationInput
            length={6}
            onChange={(value) => {
              setState({
                ...state,
                one_time_password: value,
              });
            }}
            value={state.one_time_password}
            placeholder="."
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <button
            className="btn btn-pill btn-white font-weight-bold opacity-90 px-15 py-3 m-2 just-white"
            onClick={
              state.one_time_password &&
              state.one_time_password.length === 6 &&
              handleSubmit
            }
            autoFocus
          >
            Confirm
          </button>
          <button
            onClick={handleCloseDialog}
            className="btn btn-pill btn-outline-white font-weight-bold opacity-70 px-15 py-3 m-2"
          >
            Cancel
          </button>
        </DialogActions>
      </Dialog>
      <div className="login login-3 login-signin-on d-flex flex-row-fluid">
        <div
          className="d-flex flex-center bgi-size-cover bgi-no-repeat flex-row-fluid"
          style={{
            backgroundColor: "rgb(1, 16, 37)",
            backgroundImage:
              "radial-gradient(50% 50% at 50% 50%, rgba(33, 114, 229, 0.3) 0%, rgb(70 132 167 / 0%) 100%)",
          }}
        >
          <div className="login-form text-center text-white p-7 position-relative overflow-hidden">
            <div className="d-flex flex-center mb-15">
              <CustomLink to="/login">
                <img src="/img/logo.svg" className="h-50px" alt="" />
              </CustomLink>
            </div>
            <div className="login-forgot">
              <div className="mb-10">
                <h3>Forgotten Password ?</h3>
                <p className="opacity-60">
                  Enter your email to reset your password
                </p>
              </div>
              <form
                className="form"
                id="kt_login_forgot_form"
                onSubmit={handleSubmit}
              >
                {state.youCanChangePassword ? (
                  <>
                    <div className="form-group">
                      <input
                        className="form-control h-auto text-white placeholder-white bg-dark-o-70 rounded-pill border-1 py-4 px-8 mb-5"
                        type="password"
                        placeholder="New Password"
                        name="password"
                        autocomplete="off"
                        value={state.password}
                        onChange={handleChange}
                      />
                      {isError("password") && (
                        <div className="fv-plugins-message-container">
                          <div
                            data-field="password"
                            data-validator="password"
                            className="fv-help-block"
                          >
                            {getErrorMessage("password")}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <input
                        className="form-control h-auto text-white placeholder-white bg-dark-o-70 rounded-pill border-1 py-4 px-8 mb-5"
                        type="password"
                        placeholder="Confirm New Password"
                        name="password_confirmation"
                        value={state.password_confirmation}
                        onChange={handleChange}
                      />
                      {isError("password_confirmation") && (
                        <div className="fv-plugins-message-container">
                          <div
                            data-field="password_confirmation"
                            data-validator="password_confirmation"
                            className="fv-help-block"
                          >
                            {getErrorMessage("password_confirmation")}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="form-group mb-10">
                    <input
                      className={`form-control h-auto text-white placeholder-white bg-dark-o-70 rounded-pill border-1 py-4 px-8${
                        isError("email") ? " is-invalid" : ""
                      }`}
                      type="text"
                      placeholder="Email"
                      name="email"
                      autocomplete="off"
                      value={state.email}
                      onChange={handleChange}
                    />
                    {isError("email") && (
                      <div className="fv-plugins-message-container">
                        <div
                          data-field="email"
                          data-validator="email"
                          className="fv-help-block"
                        >
                          {getErrorMessage("email")}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="form-group">
                  <button
                    id="kt_login_forgot_submit"
                    className="btn btn-pill btn-white font-weight-bold opacity-90 px-15 py-3 m-2 just-white"
                    disabled={loading}
                    type="submit"
                  >
                    {state.youCanChangePassword ? "Confirm" : "Request"}
                  </button>
                  <button
                    id="kt_login_forgot_cancel"
                    className="btn btn-pill btn-outline-white font-weight-bold opacity-70 px-15 py-3 m-2"
                    onClick={() => navigate("/login")}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
